/**
 * Per-layer analytics modal.
 *
 * Side-nav layout — 7 analyses grouped into ATTRIBUTE / SPATIAL / ADVANCED.
 * Each tab renders its own form into `#amBody`. On Run, results are pinned
 * to the dashboard (top of right panel) via analyticsResults.js.
 */
import {
  runStats, runFilter, runSpatialJoin, runBuffer,
  runNearest, runHotspot, runZonal
} from '../api/analytics.js';
import { listLayers } from '../api/layers.js';
import { toast } from './toast.js';
import {
  pushStatsResult, pushFilterResult, pushSpatialJoinResult,
  pushBufferResult, pushNearestResult, pushHotspotResult, pushZonalResult
} from './analyticsResults.js';

const NUMERIC_DTYPES = ['int8','int16','int32','int64','float32','float64','bool'];
const isNumeric = (dtype) => NUMERIC_DTYPES.some(t => (dtype || '').includes(t));

// Side-nav: { id → { group, label, render } }
const TABS = {
  stats:    { group: 'Attribute', label: 'Statistics' },
  filter:   { group: 'Attribute', label: 'Filter' },
  join:     { group: 'Spatial',   label: 'Spatial join' },
  buffer:   { group: 'Spatial',   label: 'Buffer' },
  nearest:  { group: 'Spatial',   label: 'Nearest distance' },
  hotspot:  { group: 'Advanced',  label: 'Hotspot (Z)' },
  zonal:    { group: 'Advanced',  label: 'Zonal raster' },
};

let currentLayer = null;
let currentTab = 'stats';
let onLayerCreated = null;
let allLayers = [];           // populated on open — for layer pickers

const html = () => `
  <div class="modal-bg" id="analyticsModal">
    <div class="modal am-modal">
      <div class="am-head">
        <h3 id="amTitle">Analyse layer</h3>
        <button class="rpanel-close" data-action="closeAnalyticsModal" title="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="am-shell">
        <nav class="am-nav" id="amNav"></nav>
        <div class="am-content" id="amBody"></div>
      </div>
    </div>
  </div>
  <style>
    .am-modal{ width:760px; max-width:96vw; padding:0; overflow:hidden; }
    .am-head{
      display:flex; align-items:baseline; justify-content:space-between;
      padding:18px 22px 14px; border-bottom:1px solid var(--border);
    }
    .am-head h3{ margin:0; }
    .am-shell{ display:grid; grid-template-columns:170px 1fr; min-height:420px; }
    .am-nav{
      border-right:1px solid var(--border); padding:14px 0;
      background:linear-gradient(180deg, var(--bg2), var(--bg2));
    }
    .am-nav-group{
      padding:14px 18px 6px; font-size:9.5px; font-weight:600;
      text-transform:uppercase; letter-spacing:0.14em; color:var(--text3);
    }
    .am-nav-group:first-child{ padding-top:4px; }
    .am-nav-item{
      display:block; width:100%; text-align:left; border:none; background:none;
      color:var(--text2); padding:8px 18px; font-size:12.5px; font-weight:500;
      letter-spacing:-0.005em; font-family:var(--font); cursor:pointer;
      border-left:2px solid transparent; transition:color .15s var(--ease), background .15s var(--ease), border-color .15s var(--ease);
    }
    .am-nav-item:hover{ color:var(--text); background:var(--bg3); }
    .am-nav-item.on{
      color:var(--text); background:var(--primary-dim);
      border-left-color:var(--primary);
    }
    .am-content{ padding:20px 22px; overflow-y:auto; max-height:70vh; }
    .am-form{ display:flex; flex-direction:column; gap:14px; }
    .am-help{ font-size:11.5px; color:var(--text3); line-height:1.5; margin:0 0 4px; }
    .am-row{ display:flex; align-items:center; gap:10px; }
    .am-row > label{
      width:96px; flex-shrink:0; font-size:9.5px; font-weight:600;
      text-transform:uppercase; letter-spacing:0.12em; color:var(--text3);
    }
    .am-row select, .am-row input{
      flex:1; background:var(--bg3); border:1px solid var(--border2); color:var(--text);
      padding:8px 10px; border-radius:var(--radius); font-size:12.5px; font-family:var(--font);
    }
    .am-row select:focus, .am-row input:focus{ outline:none; border-color:var(--primary); }
    .am-cond-row{
      display:grid; grid-template-columns:1fr 90px 1fr 28px; gap:8px; align-items:center;
    }
    .am-cond-add{
      align-self:flex-start; font-size:10.5px; font-weight:600; color:var(--primary);
      background:none; border:none; padding:6px 0; letter-spacing:-0.005em; cursor:pointer;
    }
    .am-cond-add:hover{ color:var(--primary-hover); }
    .am-cond-del{
      width:28px; height:28px; border-radius:var(--radius-sm); border:1px solid var(--border2);
      background:var(--bg3); color:var(--text3); display:grid; place-items:center; cursor:pointer;
    }
    .am-cond-del:hover{ color:var(--danger); border-color:var(--danger); }
    .am-actions{
      display:flex; justify-content:flex-end; gap:8px;
      margin-top:14px; padding-top:14px; border-top:1px solid var(--border);
    }
    .am-error{ font-size:11.5px; min-height:14px; }
    .am-error:empty{ display:none; }
    .am-summary{
      background:var(--bg3); border:1px solid var(--border); border-radius:var(--radius-lg);
      padding:14px 16px; margin-top:14px;
    }
    .am-summary .lbl{
      font-size:9.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.12em;
      color:var(--text3); margin-bottom:4px;
    }
    .am-summary .val{
      font-family:var(--font-display); font-weight:500; font-size:24px;
      letter-spacing:-0.02em; color:var(--primary); font-feature-settings:'tnum';
    }
    .am-checkbox-row{ display:flex; gap:14px; flex-wrap:wrap; }
    .am-checkbox-row label{
      display:inline-flex; align-items:center; gap:6px; font-size:11.5px;
      color:var(--text2); cursor:pointer;
    }
  </style>
`;

function ensureMounted() {
  if (document.getElementById('analyticsModal')) return;
  document.body.insertAdjacentHTML('beforeend', html());
  const root = document.getElementById('analyticsModal');
  root.addEventListener('click', e => {
    const tabBtn = e.target.closest('[data-am-tab]');
    if (tabBtn) { selectTab(tabBtn.dataset.amTab); return; }
    if (e.target === root) closeModal();
  });
}

function renderNav() {
  const groups = {};
  for (const [id, t] of Object.entries(TABS)) {
    (groups[t.group] ||= []).push({ id, label: t.label });
  }
  const html = Object.entries(groups).map(([groupName, items]) => `
    <div class="am-nav-group">${groupName}</div>
    ${items.map(it => `
      <button class="am-nav-item ${it.id === currentTab ? 'on' : ''}" data-am-tab="${it.id}">${it.label}</button>
    `).join('')}
  `).join('');
  document.getElementById('amNav').innerHTML = html;
}

function selectTab(id) {
  currentTab = id;
  document.querySelectorAll('.am-nav-item').forEach(el =>
    el.classList.toggle('on', el.dataset.amTab === id)
  );
  renderTab();
}

export async function openAnalyticsModal(layer, opts = {}) {
  ensureMounted();
  currentLayer = layer;
  currentTab = 'stats';
  onLayerCreated = opts.onLayerCreated || null;
  document.getElementById('amTitle').textContent = `Analyse · ${layer.name}`;
  renderNav();
  document.getElementById('analyticsModal').classList.add('show');
  // Load other layers for spatial pickers (filter to ones the user owns + ready)
  try { allLayers = (await listLayers()).filter(l => l.status === 'ready'); }
  catch { allLayers = []; }
  renderTab();
}

export function closeAnalyticsModal() { closeModal(); }
function closeModal() { document.getElementById('analyticsModal')?.classList.remove('show'); }

function renderTab() {
  const body = document.getElementById('amBody');
  const renderers = {
    stats:    renderStatsForm,
    filter:   renderFilterForm,
    join:     renderJoinForm,
    buffer:   renderBufferForm,
    nearest:  renderNearestForm,
    hotspot:  renderHotspotForm,
    zonal:    renderZonalForm,
  };
  body.innerHTML = renderers[currentTab]();
  attachHandlers();
}

// =========================================================================
// Tab: Statistics
// =========================================================================

function renderStatsForm() {
  const cols = Object.entries(currentLayer.attribute_schema || {});
  const numericCols = cols.filter(([, dtype]) => isNumeric(dtype));
  return `
    <div class="am-form">
      <p class="am-help">Aggregate any column. Group by a categorical column to see breakdowns.</p>
      <div class="am-row">
        <label>Metric</label>
        <select id="amMetric">
          <option value="count">Count</option>
          <option value="sum">Sum</option>
          <option value="avg">Average</option>
          <option value="min">Min</option>
          <option value="max">Max</option>
          <option value="stddev">Std. deviation</option>
        </select>
      </div>
      <div class="am-row" id="amColumnRow" style="display:none">
        <label>Of column</label>
        <select id="amColumn">
          ${numericCols.map(([c]) => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Group by</label>
        <select id="amGroupBy">
          <option value="">— none (overall) —</option>
          ${cols.map(([c]) => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runStatsClick() {
  const metric  = document.getElementById('amMetric').value;
  const column  = document.getElementById('amColumn')?.value;
  const groupBy = document.getElementById('amGroupBy').value || null;
  const payload = { metric, group_by: groupBy };
  if (metric !== 'count') payload.column = column;
  const result = await runStats(currentLayer.id, payload);
  pushStatsResult(currentLayer, payload, result);
  toast('Result pinned to dashboard');
}

// =========================================================================
// Tab: Filter
// =========================================================================

function renderFilterForm() {
  return `
    <div class="am-form">
      <p class="am-help">Select features matching one or more conditions. Save matches as a new layer to chain further analyses.</p>
      <div id="amConditions" style="display:flex;flex-direction:column;gap:8px">
        ${renderConditionRow()}
      </div>
      <button id="amCondAdd" class="am-cond-add">+ add condition</button>
      <div class="am-row">
        <label>Combine</label>
        <select id="amCombinator">
          <option value="AND">All must match (AND)</option>
          <option value="OR">Any can match (OR)</option>
        </select>
      </div>
      <div class="am-row">
        <label>Save as</label>
        <input id="amSaveAs" type="text" placeholder="(optional) new layer name">
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-outline" id="amPreview">Preview</button>
        <button class="btn-sm btn-run" id="amRun">Save as layer</button>
      </div>
    </div>
  `;
}

/**
 * One unique <datalist> id per condition row so each row's value input
 * gets its own column-specific suggestions.
 */
let _conditionRowSeq = 0;

function renderConditionRow() {
  const cols = Object.keys(currentLayer.attribute_schema || {});
  const dlId = `am-vals-${++_conditionRowSeq}`;
  return `
    <div class="am-cond-row">
      <select class="am-cond-col">
        ${cols.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
      </select>
      <select class="am-cond-op">
        <option>=</option><option>!=</option>
        <option>&gt;</option><option>&gt;=</option>
        <option>&lt;</option><option>&lt;=</option>
        <option>LIKE</option><option>ILIKE</option>
        <option>IS NULL</option><option>IS NOT NULL</option>
      </select>
      <input class="am-cond-val" type="text" placeholder="value" list="${dlId}">
      <datalist id="${dlId}"></datalist>
      <button class="am-cond-del" title="Remove">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
  `;
}

/**
 * Populate a row's <datalist> with the top distinct values of the chosen
 * column. Cached per layer+column so reopening the same column doesn't
 * re-hit the API.
 */
const _categoriesCache = new Map();   // key: `${layerId}:${col}` → string[]

async function loadValuesForRow(row) {
  const colSelect = row.querySelector('.am-cond-col');
  const datalist  = row.querySelector('datalist');
  if (!colSelect || !datalist) return;

  const col = colSelect.value;
  const cacheKey = `${currentLayer.id}:${col}`;
  let values = _categoriesCache.get(cacheKey);

  if (!values) {
    try {
      const { runCategories } = await import('../api/analytics.js');
      const res = await runCategories(currentLayer.id, { column: col, limit: 200 });
      values = (res.values || []).map(v => v.value).filter(v => v !== null);
      _categoriesCache.set(cacheKey, values);
    } catch {
      values = [];
    }
  }

  datalist.innerHTML = values
    .map(v => `<option value="${escape(String(v))}">`)
    .join('');
}

function gatherConditions() {
  return [...document.querySelectorAll('#amConditions .am-cond-row')].map(row => {
    const column = row.querySelector('.am-cond-col').value;
    const op     = row.querySelector('.am-cond-op').value;
    let value    = row.querySelector('.am-cond-val').value;
    if (value !== '' && !isNaN(Number(value))) value = Number(value);
    if (value === '') value = null;
    return { column, op, value };
  });
}

async function runFilterClick(asSave) {
  const conditions = gatherConditions();
  const combinator = document.getElementById('amCombinator').value;
  const saveAs     = document.getElementById('amSaveAs').value.trim();
  if (asSave && !saveAs) throw new Error('Enter a name for the new layer.');
  const payload = { conditions, combinator, limit: 5000, ...(asSave ? { save_as: saveAs } : {}) };
  const result = await runFilter(currentLayer.id, payload);
  if (asSave) {
    pushFilterResult(currentLayer, payload, { matched_count: result.feature_count }, result);
    toast(`Saved "${result.name}" as a new layer`);
    onLayerCreated?.(result);
  } else {
    pushFilterResult(currentLayer, payload, result);
    toast('Result pinned to dashboard');
  }
}

// =========================================================================
// Tab: Spatial join
// =========================================================================

function otherVectorLayers() {
  return allLayers.filter(l => l.id !== currentLayer.id && l.layer_type === 'vector');
}

function renderJoinForm() {
  const others = otherVectorLayers();
  const labelCols = Object.keys(currentLayer.attribute_schema || {});
  return `
    <div class="am-form">
      <p class="am-help">For every feature in <b>${escape(currentLayer.name)}</b>, aggregate features from another layer that satisfy a spatial relation. Classic use: count points per district.</p>
      <div class="am-row">
        <label>From layer</label>
        <select id="amTarget">
          ${others.map(l => `<option value="${l.id}">${escape(l.name)} (${escape(l.geometry_type || '?')})</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Relation</label>
        <select id="amRelation">
          <option value="within">Target features within source</option>
          <option value="intersects">Target features intersect source</option>
          <option value="contains">Source contains target features</option>
        </select>
      </div>
      <div class="am-row">
        <label>Aggregate</label>
        <select id="amAgg">
          <option value="count">Count</option>
          <option value="sum">Sum</option>
          <option value="avg">Average</option>
          <option value="min">Min</option>
          <option value="max">Max</option>
        </select>
      </div>
      <div class="am-row" id="amTargetColRow" style="display:none">
        <label>Of column</label>
        <select id="amTargetCol"></select>
      </div>
      <div class="am-row">
        <label>Label by</label>
        <select id="amLabel">
          <option value="">— row index —</option>
          ${labelCols.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runJoinClick() {
  const target_layer_id = +document.getElementById('amTarget').value;
  const relation        = document.getElementById('amRelation').value;
  const aggregate       = document.getElementById('amAgg').value;
  const label_column    = document.getElementById('amLabel').value || null;
  const target_column   = aggregate === 'count' ? null
                          : document.getElementById('amTargetCol').value || null;
  const payload = { target_layer_id, relation, aggregate, target_column, label_column };
  const result = await runSpatialJoin(currentLayer.id, payload);
  const targetLayer = allLayers.find(l => l.id === target_layer_id);
  pushSpatialJoinResult(currentLayer, targetLayer, payload, result);
  toast('Spatial join pinned to dashboard');
}

// =========================================================================
// Tab: Buffer
// =========================================================================

function renderBufferForm() {
  const others = otherVectorLayers();
  const labelCols = Object.keys(currentLayer.attribute_schema || {});
  return `
    <div class="am-form">
      <p class="am-help">For every feature in this layer, draw a metric buffer and (optionally) count features from another layer that fall inside.</p>
      <div class="am-row">
        <label>Radius</label>
        <input id="amRadius" type="number" step="100" min="100" value="5000"> <span style="color:var(--text3);font-size:11px">metres</span>
      </div>
      <div class="am-row">
        <label>Count from</label>
        <select id="amTarget">
          <option value="">— buffer area only —</option>
          ${others.map(l => `<option value="${l.id}">${escape(l.name)} (${escape(l.geometry_type || '?')})</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Label by</label>
        <select id="amLabel">
          <option value="">— row index —</option>
          ${labelCols.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runBufferClick() {
  const radius_m       = +document.getElementById('amRadius').value;
  const target_layer_id = +document.getElementById('amTarget').value || null;
  const label_column   = document.getElementById('amLabel').value || null;
  const payload = { radius_m, target_layer_id, label_column };
  const result = await runBuffer(currentLayer.id, payload);
  const targetLayer = target_layer_id ? allLayers.find(l => l.id === target_layer_id) : null;
  pushBufferResult(currentLayer, targetLayer, payload, result);
  toast('Buffer result pinned to dashboard');
}

// =========================================================================
// Tab: Nearest distance
// =========================================================================

function renderNearestForm() {
  const others = otherVectorLayers();
  const labelCols = Object.keys(currentLayer.attribute_schema || {});
  return `
    <div class="am-form">
      <p class="am-help">For every feature in this layer, compute the distance (km) to its single nearest feature in another layer.</p>
      <div class="am-row">
        <label>Nearest in</label>
        <select id="amTarget">
          ${others.map(l => `<option value="${l.id}">${escape(l.name)} (${escape(l.geometry_type || '?')})</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Label by</label>
        <select id="amLabel">
          <option value="">— row index —</option>
          ${labelCols.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Limit</label>
        <input id="amLimit" type="number" min="1" max="10000" value="200">
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runNearestClick() {
  const target_layer_id = +document.getElementById('amTarget').value;
  const label_column    = document.getElementById('amLabel').value || null;
  const limit           = +document.getElementById('amLimit').value;
  const payload = { target_layer_id, label_column, limit };
  const result = await runNearest(currentLayer.id, payload);
  const targetLayer = allLayers.find(l => l.id === target_layer_id);
  pushNearestResult(currentLayer, targetLayer, payload, result);
  toast('Nearest distances pinned to dashboard');
}

// =========================================================================
// Tab: Hotspot (local Z)
// =========================================================================

function renderHotspotForm() {
  const cols = Object.entries(currentLayer.attribute_schema || {});
  const numericCols = cols.filter(([, dtype]) => isNumeric(dtype));
  return `
    <div class="am-form">
      <p class="am-help">Find features whose value differs significantly from their K-nearest neighbours. |z| ≥ 1.96 ≈ 95% confidence the feature is a hot or cold spot.</p>
      <div class="am-row">
        <label>Column</label>
        <select id="amColumn">
          ${numericCols.map(([c]) => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Neighbours K</label>
        <input id="amK" type="number" min="2" max="50" value="8">
      </div>
      <div class="am-row">
        <label>Label by</label>
        <select id="amLabel">
          <option value="">— row index —</option>
          ${cols.map(([c]) => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runHotspotClick() {
  const column = document.getElementById('amColumn').value;
  const k      = +document.getElementById('amK').value;
  const label_column = document.getElementById('amLabel').value || null;
  const payload = { column, k, label_column };
  const result = await runHotspot(currentLayer.id, payload);
  pushHotspotResult(currentLayer, payload, result);
  toast(`${result.hot_count} hot · ${result.cold_count} cold`);
}

// =========================================================================
// Tab: Zonal raster stats
// =========================================================================

function renderZonalForm() {
  const rasters = allLayers.filter(l => l.layer_type === 'raster');
  const labelCols = Object.keys(currentLayer.attribute_schema || {});
  if (rasters.length === 0) {
    return `
      <div class="am-form">
        <p class="am-help">No raster layers uploaded yet. Upload a GeoTIFF first, then come back here.</p>
        <div class="am-actions">
          <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        </div>
      </div>
    `;
  }
  if (currentLayer.layer_type !== 'vector' || !/Polygon/i.test(currentLayer.geometry_type || '')) {
    return `
      <div class="am-form">
        <p class="am-help">Zonal stats need a polygon layer to define the zones. Open this from a polygon layer in My Layers.</p>
        <div class="am-actions">
          <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        </div>
      </div>
    `;
  }
  return `
    <div class="am-form">
      <p class="am-help">For every polygon in this layer, compute summary statistics of the chosen raster's pixels that fall within.</p>
      <div class="am-row">
        <label>Raster</label>
        <select id="amRaster">
          ${rasters.map(l => `<option value="${l.id}">${escape(l.name)}</option>`).join('')}
        </select>
      </div>
      <div class="am-row">
        <label>Stats</label>
        <div class="am-checkbox-row" id="amStats">
          ${['mean','min','max','sum','median','stddev','count'].map(s =>
            `<label><input type="checkbox" value="${s}" ${['mean','min','max'].includes(s)?'checked':''}> ${s}</label>`
          ).join('')}
        </div>
      </div>
      <div class="am-row">
        <label>Band</label>
        <input id="amBand" type="number" min="1" value="1">
      </div>
      <div class="am-row">
        <label>Label by</label>
        <select id="amLabel">
          <option value="">— row index —</option>
          ${labelCols.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('')}
        </select>
      </div>
      <div class="am-error" id="amErr"></div>
      <div class="am-actions">
        <button class="btn-sm btn-outline" data-action="closeAnalyticsModal">Close</button>
        <button class="btn-sm btn-run" id="amRun">Run</button>
      </div>
    </div>
  `;
}

async function runZonalClick() {
  const raster_layer_id = +document.getElementById('amRaster').value;
  const band            = +document.getElementById('amBand').value;
  const label_column    = document.getElementById('amLabel').value || null;
  const stats = [...document.querySelectorAll('#amStats input:checked')].map(i => i.value);
  if (stats.length === 0) throw new Error('Pick at least one statistic.');
  const payload = { raster_layer_id, band, stats, label_column };
  const result = await runZonal(currentLayer.id, payload);
  const raster = allLayers.find(l => l.id === raster_layer_id);
  pushZonalResult(currentLayer, raster, payload, result);
  toast('Zonal stats pinned to dashboard');
}

// =========================================================================
// Wire-up
// =========================================================================

function attachHandlers() {
  const err = document.getElementById('amErr');

  if (currentTab === 'stats') {
    const metricEl = document.getElementById('amMetric');
    const colRow   = document.getElementById('amColumnRow');
    const sync = () => { colRow.style.display = metricEl.value === 'count' ? 'none' : 'flex'; };
    metricEl.addEventListener('change', sync); sync();
    bindRun(runStatsClick);
  }
  else if (currentTab === 'filter') {
    const cont = document.getElementById('amConditions');
    cont.addEventListener('click', e => {
      const del = e.target.closest('.am-cond-del');
      if (del) del.closest('.am-cond-row').remove();
    });
    // Refresh the row's datalist whenever the column changes
    cont.addEventListener('change', e => {
      const colSelect = e.target.closest('.am-cond-col');
      if (colSelect) loadValuesForRow(colSelect.closest('.am-cond-row'));
    });
    // Lazy-load on focus too — first interaction triggers the fetch
    cont.addEventListener('focusin', e => {
      const valInput = e.target.closest('.am-cond-val');
      if (valInput) {
        const row = valInput.closest('.am-cond-row');
        const dl = row.querySelector('datalist');
        if (dl && !dl.children.length) loadValuesForRow(row);
      }
    });
    // Pre-populate the first row's suggestions
    cont.querySelectorAll('.am-cond-row').forEach(loadValuesForRow);
    document.getElementById('amCondAdd').addEventListener('click', () => {
      cont.insertAdjacentHTML('beforeend', renderConditionRow());
      const newRow = cont.lastElementChild;
      if (newRow) loadValuesForRow(newRow);
    });
    document.getElementById('amPreview').addEventListener('click', () => safeRun(() => runFilterClick(false)));
    document.getElementById('amRun').addEventListener('click', () => safeRun(() => runFilterClick(true)));
  }
  else if (currentTab === 'join') {
    const aggEl = document.getElementById('amAgg');
    const colRow = document.getElementById('amTargetColRow');
    const colSel = document.getElementById('amTargetCol');
    const targetEl = document.getElementById('amTarget');
    const repopulate = () => {
      const target = allLayers.find(l => l.id === +targetEl.value);
      const numerics = Object.entries(target?.attribute_schema || {})
        .filter(([, d]) => isNumeric(d)).map(([c]) => c);
      colSel.innerHTML = numerics.map(c => `<option value="${escape(c)}">${escape(c)}</option>`).join('');
      colRow.style.display = aggEl.value === 'count' ? 'none' : 'flex';
    };
    aggEl.addEventListener('change', repopulate);
    targetEl.addEventListener('change', repopulate);
    repopulate();
    bindRun(runJoinClick);
  }
  else if (currentTab === 'buffer')  bindRun(runBufferClick);
  else if (currentTab === 'nearest') bindRun(runNearestClick);
  else if (currentTab === 'hotspot') bindRun(runHotspotClick);
  else if (currentTab === 'zonal')   bindRun(runZonalClick);
}

function bindRun(handler) {
  const btn = document.getElementById('amRun');
  if (!btn) return;
  btn.addEventListener('click', () => safeRun(handler));
}

async function safeRun(fn) {
  const err = document.getElementById('amErr');
  const btn = document.getElementById('amRun');
  if (err) err.innerHTML = '';
  if (btn) btn.classList.add('btn-loading');
  try { await fn(); }
  catch (e) {
    if (err) err.innerHTML = `<div class="alert alert-error">${escape(e.message)}</div>`;
    else toast(e.message);
  }
  finally {
    if (btn) btn.classList.remove('btn-loading');
  }
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
}
